import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Alert, LinearProgress, CircularProgress } from '@mui/material';
import { getMerchantSubscription, getMerchantTokenBalance } from '../../../../services/subscriptionAPI';
import { merchantAuthAPI } from '../../../../services/merchantAPI';

const PURPLE = '#6318C6';
const BORDER = '#e5e7eb';

export default function OutOfTokens() {
  const navigate  = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sub, setSub]         = useState(null);
  const [balance, setBalance] = useState(0);
  const [error, setError]     = useState(null);
  const [showWhatsAppSmsAuth, setShowWhatsAppSmsAuth] = useState(true);

  useEffect(() => {
    merchantAuthAPI.checkAuthVisibility()
      .then(r => { if (typeof r.data?.show_whatsapp_sms_auth === 'boolean') setShowWhatsAppSmsAuth(r.data.show_whatsapp_sms_auth); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([getMerchantSubscription(), getMerchantTokenBalance()])
      .then(([subRes, tokRes]) => {
        setSub(subRes.data?.subscription || null);
        setBalance(tokRes.data?.token_balance || 0);
      })
      .catch(() => setError('Failed to load your subscription data. Please refresh the page.'))
      .finally(() => setLoading(false));
  }, []);

  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  const isGrace     = sub?.status === 'grace';
  const graceExpiry = sub?.grace_expiry_date ? fmtDate(sub.grace_expiry_date) : null;
  const bundled     = sub?.bundled_tokens || 0;
  const usedPct     = bundled > 0 ? Math.min(100, Math.round(((bundled - balance) / bundled) * 100)) : 100;

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
      <CircularProgress sx={{ color: PURPLE }} />
    </Box>
  );

  if (error) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10, px: 3 }}>
      <Alert severity="error" sx={{ maxWidth: 520, width: '100%' }}>{error}</Alert>
    </Box>
  );

  return (
    <Box sx={{ background: '#f1f3f5', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Box sx={{ maxWidth: 520, width: '100%' }}>

        <Box sx={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <Box sx={{ height: 4, background: 'linear-gradient(90deg, #6318C6, #e91e8c, #f59e0b)' }} />

          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            {/* Heading */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography sx={{ fontSize: 56, lineHeight: 1, mb: 2 }}>🪙</Typography>
              <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#111827', mb: 1 }}>
                You've run out of tokens
              </Typography>
              <Typography sx={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6 }}>
                Your token balance is <strong>0</strong>. Add more tokens to continue generating AI content.
              </Typography>
            </Box>

            {/* Usage bar */}
            {bundled > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography sx={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Tokens used this period</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{usedPct}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={usedPct}
                  sx={{
                    height: 8, borderRadius: 4, backgroundColor: '#f3f4f6',
                    '& .MuiLinearProgress-bar': { borderRadius: 4, background: 'linear-gradient(90deg, #6318C6, #e91e8c)' },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                  <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>0</Typography>
                  <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>{bundled.toLocaleString('en-IN')} bundled</Typography>
                </Box>
              </Box>
            )}

            {/* Grace warning */}
            {isGrace && graceExpiry && (
              <Alert severity="warning" sx={{ mb: 3, borderRadius: 2, fontSize: 13 }}>
                <strong>Grace period active.</strong> Tokens preserved until <strong>{graceExpiry}</strong>. Renew before that date to keep your balance.
              </Alert>
            )}

            {!sub && (
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2, fontSize: 13 }}>
                You don't have an active subscription. Choose a plan to get started.
              </Alert>
            )}

            {/* CTAs */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {showWhatsAppSmsAuth ? (
                <>
                  <Button
                    fullWidth variant="contained"
                    onClick={() => navigate('/credits?tab=0')}
                    sx={{ background: PURPLE, borderRadius: '10px', fontWeight: 700, fontSize: 14, py: 1.5, textTransform: 'none', '&:hover': { background: '#5213a8' } }}>
                    Buy More Tokens
                  </Button>
                  <Button
                    fullWidth variant="outlined"
                    onClick={() => navigate('/credits?open=1')}
                    sx={{ borderColor: BORDER, color: '#374151', borderRadius: '10px', fontWeight: 600, fontSize: 14, py: 1.25, textTransform: 'none',
                      '&:hover': { borderColor: PURPLE, color: PURPLE, background: '#f3eeff' } }}>
                    View All Plans
                  </Button>
                  <Button
                    fullWidth variant="text"
                    onClick={() => navigate('/credits?tab=0')}
                    sx={{ color: '#9ca3af', borderRadius: '10px', fontWeight: 600, fontSize: 13, py: 1, textTransform: 'none', '&:hover': { color: '#6b7280', background: '#f9fafb' } }}>
                    View Subscription Details
                  </Button>
                </>
              ) : (
                <Typography sx={{ fontSize: 14, color: '#6b7280', textAlign: 'center', mt: 1 }}>
                  Visit our website to manage your subscription.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Typography sx={{ textAlign: 'center', fontSize: 12, color: '#9ca3af', mt: 2 }}>
          Need help? Contact us at <span style={{ color: PURPLE, fontWeight: 600 }}>support@glammify.ai</span>
        </Typography>
      </Box>
    </Box>
  );
}
